import { ref } from 'vue'
import { s__ } from '@/locale'

const IMAGE_EXTENSION_BY_TYPE = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
}

function normalizeClipboardImage(file) {
  if (file.name) return file

  const extension = IMAGE_EXTENSION_BY_TYPE[file.type] || 'png'
  return new File([file], `clipboard-image-${Date.now()}.${extension}`, { type: file.type })
}

function appendAttachableContext(formData, context = {}) {
  if (context.attachmentDraftId) {
    formData.append('attachment_draft_id', context.attachmentDraftId)
    return
  }

  if (context.attachableType === 'task' && context.attachableId) {
    formData.append('task_id', context.attachableId)
  } else if (context.attachableType === 'comment' && context.attachableId) {
    formData.append('comment_id', context.attachableId)
  } else if (context.attachableType === 'taskTemplate' && context.attachableId) {
    formData.append('task_template_id', context.attachableId)
  } else if (context.projectId) {
    formData.append('project_id', context.projectId)
  }

  if (context.purpose) {
    formData.append('purpose', context.purpose)
  }
}

export function useClipboardImageUpload() {
  const uploading = ref(false)
  const error = ref('')

  const uploadClipboardImage = async (file, context = {}) => {
    const image = normalizeClipboardImage(file)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
    const formData = new FormData()
    formData.append('files[]', image)
    appendAttachableContext(formData, context)

    uploading.value = true
    error.value = ''

    try {
      const response = await fetch('/attachments/inline', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
        credentials: 'same-origin',
      })

      const data = await response.json()
      if (!response.ok || data.errors?.length) {
        throw new Error(data.errors?.join(', ') || s__('Attachments|Failed to upload attachments'))
      }

      const attachment = data.attachments?.[0]
      if (!attachment) {
        throw new Error(s__('Attachments|Failed to upload attachments'))
      }

      const filename = attachment.filename || image.name
      const url = attachment.download_url || attachment.downloadUrl
      return {
        attachment,
        attachmentDraftId: data.attachment_draft_id || null,
        markdown: `![${filename}](${url})`,
      }
    } catch (uploadError) {
      error.value = uploadError.message || s__('Attachments|Failed to upload attachments')
      throw uploadError
    } finally {
      uploading.value = false
    }
  }

  return {
    uploading,
    error,
    uploadClipboardImage,
  }
}
